import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProductById, submitReview } from '../../../api/productApi';
import { ProductDetail } from '../../../types/product';
import { LoadingIndicator } from '../../../components/LoadingIndicator/LoadingIndicator';
import { NavigationTabs } from '../../../components/NavigationTabs/NavigationTabs';
import { BackButton } from '../../../components/BackButton/BackButton';
import styles from './MyReview.module.scss';

export const MyReview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [rating, setRating] = useState<number>(0);
    const [reviewText, setReviewText] = useState<string>('');
    const [product, setProduct] = useState<ProductDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('account');

    useEffect(() => {
        const loadProduct = async () => {
            if (!id) return;

            try {
                const productData = await fetchProductById(id);
                setProduct(productData);
            } catch (err) {
                setError('Не удалось загрузить информацию о курсе');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadProduct();
    }, [id]);

    const handleSubmit = async () => {
        if (!id) return;

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            await submitReview({
                productId: id,
                rating,
                text: reviewText
            });
            navigate('/my-reviews');
        } catch (err) {
            setSubmitError('Не удалось отправить отзыв. Пожалуйста, попробуйте позже.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setActiveTab(tab);
        if (tab === 'products') navigate('/');
        else if (tab === 'favorites') navigate('/favorites');
        else if (tab === 'account') navigate('/account');
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingIndicator message="Загрузка..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <BackButton />
                <div className={styles.error}>
                    <p>{error}</p>
                    <button
                        className={styles.retryButton}
                        onClick={() => window.location.reload()}
                    >
                        Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className={styles.errorContainer}>
                <BackButton />
                <div className={styles.error}>
                    <p>Курс не найден</p>
                    <button
                        className={styles.retryButton}
                        onClick={() => navigate('/my-reviews')}
                    >
                        Вернуться к отзывам
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <BackButton className={styles.backButton} />

            <div className={styles.productInfo}>
                <img
                    src={product.images[0]}
                    alt={product.title}
                    className={styles.productImage}
                />
                <div className={styles.productDetails}>
                    <h1 className={styles.productTitle}>{product.title}</h1>
                    <div className={styles.authorInfo}>
                        <span className={styles.authorLabel}>Автор</span>
                        <span className={styles.authorName}>{product.author}</span>
                    </div>
                </div>
            </div>

            <div className={styles.ratingBlock}>
                <h2 className={styles.ratingTitle}>Оцените курс</h2>
                <div className={styles.starsContainer}>
                    {[1, 2, 3, 4, 5].map((star) => (
                        <span
                            key={star}
                            className={`${styles.star} ${star <= rating ? styles.active : ''}`}
                            onClick={() => setRating(star)}
                        >
                            ★
                        </span>
                    ))}
                </div>
            </div>

            <div className={styles.reviewBlock}>
                <h2 className={styles.reviewTitle}>Как вам курс?</h2>
                <textarea
                    className={styles.reviewInput}
                    placeholder="Что понравилось?"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                />
            </div>

            {submitError && <div className={styles.submitError}>{submitError}</div>}

            <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={rating === 0 || reviewText.trim() === '' || isSubmitting}
            >
                {isSubmitting ? 'Отправка...' : 'Оставить отзыв'}
            </button>

            <footer className={styles.footer}>
                <NavigationTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </footer>
        </div>
    );
};